import React from "react";
import { Card, Button, Form } from "react-bootstrap";
import { LuX, LuPlus, LuTrash2 } from "react-icons/lu";
import Utils from "context/utils";

// ---------------------------------------------------------------------------
// Pure helper — exported for unit testing
// ---------------------------------------------------------------------------

/**
 * Diff originalProps against editedProps and produce mutation operations.
 *
 * Rules:
 *   - Changed or added keys  → set_*_props ({ op, element_id, props: {key: newVal}, if_rev })
 *   - Removed keys           → remove_*_props ({ op, element_id, keys: [...], if_rev })
 *   - Unchanged keys         → not included
 *   - If no diff             → returns []
 *
 * @param {"vertex"|"edge"} type
 * @param {string} elementId
 * @param {number} rev
 * @param {Object} originalProps
 * @param {Object} editedProps
 * @returns {Array} mutation operations
 */
export function propsToOperations(type, elementId, rev, originalProps, editedProps) {
  const setOp = type === "vertex" ? "set_vertex_props" : "set_edge_props";
  const removeOp = type === "vertex" ? "remove_vertex_props" : "remove_edge_props";

  const ops = [];

  // Changed / added keys
  const changedProps = {};
  for (const key of Object.keys(editedProps)) {
    if (originalProps[key] !== editedProps[key]) {
      changedProps[key] = editedProps[key];
    }
  }

  // Removed keys
  const removedKeys = Object.keys(originalProps).filter(k => !(k in editedProps));

  if (Object.keys(changedProps).length > 0) {
    ops.push({ op: setOp, element_id: elementId, props: changedProps, if_rev: rev });
  }

  if (removedKeys.length > 0) {
    ops.push({ op: removeOp, element_id: elementId, keys: removedKeys, if_rev: rev });
  }

  return ops;
}

// ---------------------------------------------------------------------------
// GraphDetailsPanel component
// ---------------------------------------------------------------------------

/**
 * GraphDetailsPanel — floating popup (bottom-left of the canvas) showing the selected element's details.
 *
 * Read-only identity fields render in a `.properties` table with `<code>` values; editable properties render
 * as inputs — consistent with the playground component details pane.
 *
 * Props:
 *   element     {Object|null} - The selected element object from the store
 *   timezone    {string}      - Timezone preference (passed from parent)
 *   onClose     {Function}    - Called when the user closes the panel
 *   onSave      {Function}    - Called with (operations[]) when user saves prop edits
 *   readOnly    {boolean}     - When true (revision not editable for the viewer) properties render as
 *                               read-only values and the add/remove/save controls are not presented
 */
class GraphDetailsPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // Edited copy of props — keys / values the user is changing in the form
      editedProps: {},
      // New-key input state
      newKey: "",
      newValue: "",
      saving: false,
    };
  }

  componentDidMount() {
    this._resetEditedProps(this.props.element);
  }

  componentDidUpdate(prevProps) {
    // Reset the edit form only when a DIFFERENT element is selected (id changes). The parent feeds a fresh
    // store copy of the same element on each store update (so read-only fields like rev stay current); we must
    // not reset the in-progress prop edits on those same-element updates.
    const prevId = prevProps.element ? prevProps.element.element_id : null;
    const currId = this.props.element ? this.props.element.element_id : null;
    if (prevId !== currId) {
      this._resetEditedProps(this.props.element);
    }
  }

  _resetEditedProps(element) {
    const props = (element && element.props) ? { ...element.props } : {};
    this.setState({ editedProps: props, newKey: "", newValue: "", saving: false });
  }

  // -------------------------------------------------------------------------
  // Prop editing actions
  // -------------------------------------------------------------------------

  handleValueChange(key, value) {
    this.setState(prev => ({
      editedProps: { ...prev.editedProps, [key]: value },
    }));
  }

  handleRemoveKey(key) {
    this.setState(prev => {
      const next = { ...prev.editedProps };
      delete next[key];
      return { editedProps: next };
    });
  }

  handleAddKey() {
    const key = this.state.newKey.trim();
    if (!key) return;
    this.setState(prev => ({
      editedProps: { ...prev.editedProps, [key]: prev.newValue },
      newKey: "",
      newValue: "",
    }));
  }

  async handleSave() {
    const { element, onSave } = this.props;
    if (!element || !onSave) return;

    const ops = propsToOperations(
      element.type,
      element.element_id,
      element.rev,
      element.props || {},
      this.state.editedProps
    );

    if (ops.length === 0) return;

    this.setState({ saving: true });
    try {
      await onSave(ops);
    } finally {
      this.setState({ saving: false });
    }
  }

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  _formatTime(time) {
    if (!time) return "—";
    const { timezone } = this.props;
    try {
      return Utils.formatDateTime(time, timezone);
    } catch (_) {
      return String(time);
    }
  }

  _hasPropChanges() {
    const { element } = this.props;
    if (!element) return false;
    const originalProps = element.props || {};
    const { editedProps } = this.state;
    const ops = propsToOperations(element.type, element.element_id, element.rev, originalProps, editedProps);
    return ops.length > 0;
  }

  render() {
    const { element, onClose, readOnly } = this.props;
    if (!element) return null;

    const { editedProps, newKey, newValue, saving } = this.state;
    const isVertex = element.type === "vertex";
    const hasPropChanges = this._hasPropChanges();
    const propKeys = Object.keys(editedProps);

    return (
      <Card className="graph-details-panel">
        <Card.Header className="graph-details-panel-header">
          {isVertex ? "Vertex Details" : "Edge Details"}
          <span className="float-right">
            <Button
              size="sm"
              variant="link"
              className="graph-details-panel-close"
              onClick={onClose}
              title="Close"
            >
              <LuX />
            </Button>
          </span>
        </Card.Header>

        <Card.Body>
          {/* General — read-only identity fields */}
          <div className="graph-details-section">General</div>
          <table className="properties">
            <tbody>
              <tr>
                <td className="prop-name-column">{isVertex ? "Vertex ID" : "Edge ID"}</td>
                <td className="prop-value-column"><code>{element.element_id}</code></td>
              </tr>
              <tr>
                <td className="prop-name-column">Type</td>
                <td className="prop-value-column"><code>{element.type}</code></td>
              </tr>
              {isVertex && (
                <tr>
                  <td className="prop-name-column">Labels</td>
                  <td className="prop-value-column">
                    {Array.isArray(element.labels) && element.labels.length > 0
                      ? element.labels.join(", ")
                      : <em className="text-muted">none</em>}
                  </td>
                </tr>
              )}
              {!isVertex && (
                <>
                  <tr>
                    <td className="prop-name-column">Label</td>
                    <td className="prop-value-column">{element.label || "—"}</td>
                  </tr>
                  <tr>
                    <td className="prop-name-column">From</td>
                    <td className="prop-value-column"><code>{element.from_id || "—"}</code></td>
                  </tr>
                  <tr>
                    <td className="prop-name-column">To</td>
                    <td className="prop-value-column"><code>{element.to_id || "—"}</code></td>
                  </tr>
                </>
              )}
              <tr>
                <td className="prop-name-column">Rev</td>
                <td className="prop-value-column">{element.rev}</td>
              </tr>
              <tr>
                <td className="prop-name-column">Created</td>
                <td className="prop-value-column">{this._formatTime(element.created_at)}</td>
              </tr>
              <tr>
                <td className="prop-name-column">Updated</td>
                <td className="prop-value-column">{this._formatTime(element.updated_at)}</td>
              </tr>
            </tbody>
          </table>

          {/* Properties — key/value pairs; editable unless the revision is read-only for this viewer. */}
          <div className="graph-details-section">Properties</div>
          <table className="properties">
            <tbody>
              {readOnly && propKeys.length === 0 && (
                <tr>
                  <td className="prop-name-column" />
                  <td className="prop-value-column"><em className="text-muted">none</em></td>
                </tr>
              )}
              {propKeys.map(key => (
                <tr key={key}>
                  <td className="prop-name-column" title={key}>{key}</td>
                  {readOnly ? (
                    <td className="prop-value-column"><code>{String(editedProps[key])}</code></td>
                  ) : (
                    <>
                      <td>
                        <Form.Control
                          size="sm"
                          className="graph-details-prop-input"
                          value={String(editedProps[key])}
                          onChange={e => this.handleValueChange(key, e.target.value)}
                        />
                      </td>
                      <td className="vertex-edge-actions-column">
                        <Button
                          size="sm"
                          variant="link"
                          className="graph-details-prop-remove"
                          title={`Remove "${key}"`}
                          onClick={() => this.handleRemoveKey(key)}
                        >
                          <LuTrash2 />
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {!readOnly && (
                <tr>
                  <td className="prop-name-column">
                    <Form.Control
                      size="sm"
                      placeholder="key"
                      className="graph-details-prop-input"
                      value={newKey}
                      onChange={e => this.setState({ newKey: e.target.value })}
                      onKeyDown={e => { if (e.key === "Enter") this.handleAddKey(); }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      placeholder="value"
                      className="graph-details-prop-input"
                      value={newValue}
                      onChange={e => this.setState({ newValue: e.target.value })}
                      onKeyDown={e => { if (e.key === "Enter") this.handleAddKey(); }}
                    />
                  </td>
                  <td className="vertex-edge-actions-column">
                    <Button
                      size="sm"
                      variant="link"
                      className="graph-details-prop-add-btn"
                      title="Add property"
                      onClick={() => this.handleAddKey()}
                      disabled={!newKey.trim()}
                    >
                      <LuPlus />
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!readOnly && hasPropChanges && (
            <Button
              size="sm"
              variant="primary"
              className="graph-details-save-btn"
              onClick={() => this.handleSave()}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          )}
        </Card.Body>
      </Card>
    );
  }
}

export default GraphDetailsPanel;
